import { useState, useRef } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { parseEmailFile, isEmailFile } from "@/components/fabric/emailFile";

export function usePdfUpload({ setEmailContent, onEmailParsed }) {
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);
  const pdfInputRef = useRef(null);

  const handlePdfUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Veuillez sélectionner un fichier PDF");
      return;
    }
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Le fichier est trop volumineux (max 20 Mo)");
      return;
    }

    setIsUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API}/pdf/extract-text`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.text && data.text.trim()) {
          setEmailContent(data.text.trim());
          toast.success(`Texte extrait de "${file.name}" - ${data.method === 'ocr' ? 'OCR utilisé' : 'Texte natif'}`);
        } else {
          toast.error("Aucun texte n'a été trouvé dans le PDF");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'extraction du PDF");
      }
    } catch (error) {
      console.error("PDF upload failed:", error);
      toast.error("Erreur lors du chargement du PDF");
    } finally {
      setIsUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleEmailUpload = async (file) => {
    setIsUploadingPdf(true);
    try {
      const data = await parseEmailFile(file);
      if (!data.text.trim()) { toast.error("Aucun texte trouvé dans l'email"); return; }
      setEmailContent(data.text);
      onEmailParsed?.(data);
      toast.success(`Email "${file.name}" importé`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleFile = (file) => (isEmailFile(file.name) ? handleEmailUpload(file) : handlePdfUpload(file));

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingPdf(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingPdf(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPdf(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.pdf') || isEmailFile(file.name)) {
        handleFile(file);
      } else {
        toast.error("Seuls les fichiers PDF, .eml et .msg sont acceptés");
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return {
    isUploadingPdf, isDraggingPdf, pdfInputRef,
    handleDragOver, handleDragLeave, handleDrop, handleFileInputChange,
  };
}
